// Global
import React, { useState, useRef, KeyboardEvent } from 'react';

// Local
import styles from './NutritionTableStandard.module.scss';
import EnhancedRichText from 'components/helpers/Atoms/EnhancedRichText/EnhancedRichText';
import useDictionary from 'lib/hooks/useDictionary';
import { useGlobalData } from 'lib/hooks/sitecore/useGlobalData';
import { IDisclaimer, INutritionItem, IVitaminItem } from './NutritionTableStandard.interfaces';
import { INormalizedRecipeNutritionData } from '../utils/recipe.utils';
import { INormalizedProductNutritionData } from '../utils/product.utils';

type INutritionFactsProps = {
  tableData: INormalizedProductNutritionData | INormalizedRecipeNutritionData;
};

export default function NutritionTableStandard({ tableData }: INutritionFactsProps) {
  // Determine if this is a product or recipe nutrition table based on presence of recipeNutrition data
  const tableType = (tableData as INormalizedRecipeNutritionData | null)?.recipeNutrition
    ? 'recipe'
    : 'product';

  const { siteName } = useGlobalData();
  const isGMFSProduct = siteName === 'GMFS' && tableType === 'product';

  // Localization dictionary items
  const { getDictionaryValue } = useDictionary();
  const nutritionHeadline = getDictionaryValue(
      'Common.NutritionFacts.Shared.NutritionFactsHeading'
    ),
    servingsPerText =
      tableType === 'product'
        ? getDictionaryValue(
            'Common.NutritionFacts.Product.ServingsSection.ServingsPerContainerTitle'
          )
        : getDictionaryValue('Common.NutritionFacts.Recipe.ServingsSection.ServingsPerTitle'),
    servingSizeText = getDictionaryValue(
      'Common.NutritionFacts.Shared.ServingsSection.ServingSizeTitle'
    ),
    amountPerServingText = getDictionaryValue(
      'Common.NutritionFacts.Shared.CaloriesSection.AmountPerServing'
    ),
    foodNutrientHeading = getDictionaryValue(
      'Common.NutritionFacts.Shared.ColumnHeadings.Nutrient'
    ),
    amountHeading = getDictionaryValue('Common.NutritionFacts.Shared.ColumnHeadings.Amount'),
    dailyValueHeading = getDictionaryValue(
      'Common.NutritionFacts.Shared.ColumnHeadings.DailyValue'
    ),
    dailyValueText = getDictionaryValue('Common.NutritionFacts.Shared.HiddenA11yText.DailyValue'),
    valueNotAvailableText = getDictionaryValue(
      'Common.NutritionFacts.Shared.HiddenA11yText.ValueNotAvailable'
    ),
    defaultDisclaimerText = getDictionaryValue(
      'Common.NutritionFacts.Shared.Disclaimers.NutritionDisclaimer'
    );

  // TODO during PDP dev: Determine if we need to handle multiple additional disclaimers for products
  const additionalDisclaimers: IDisclaimer[] = [];

  // Text items that need special handling for products vs recipes
  const caloriesText =
    'product' === tableType
      ? getDictionaryValue('Common.NutritionFacts.Product.NutrientNames.Calories')
      : (tableData as INormalizedRecipeNutritionData).recipeNutrition.caloriesText;

  // When second column data becomes available this 'asPrepared' property can be changed to match whatever it is in the API.
  const tabData = {
    tabOne:
      'product' === tableType
        ? (tableData as INormalizedProductNutritionData)
        : (tableData as INormalizedRecipeNutritionData).recipeNutrition,
    tabTwo: null as INormalizedProductNutritionData | null, //(tableData as INormalizedProductNutritionData)[1] ?? null,
  };

  // Tab navigation button labels and tab table header subtext (shows below Amount, % DV*)
  const tabBtnText = [
    'product' === tableType ? (tableData as INormalizedProductNutritionData).servingHeader1 : '',
    'product' === tableType ? tabData.tabTwo?.servingHeader1 : '',
  ];
  const tabSubText = [
    'product' === tableType ? (tableData as INormalizedProductNutritionData).servingHeader1 : '',
    'product' === tableType ? tabData.tabOne.servingSize : '',
  ];

  const disclaimerCount = additionalDisclaimers.length ? 1 + additionalDisclaimers.length : 1;
  const disclaimerIndicators = String.fromCharCode(0x002a).repeat(disclaimerCount);

  const [tabActiveIndex, setTabActiveIndex] = useState(0);
  const [tabFocusIndex, setTabFocusIndex] = useState(0);
  const tabRefs = useRef<HTMLButtonElement[]>([]);

  const tabOnClicked = (index: number) => {
    setTabActiveIndex(index);
  };

  const setTabFocus = (index: number) => {
    const tab = tabRefs.current[index];
    if (tab) {
      tab.focus();
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const tabCount = Object.keys(tabData).length;
    let tabFocus = 0;

    switch (event.code) {
      case 'ArrowRight':
        event.preventDefault();
        tabFocus = tabFocusIndex + 1;

        if (tabFocus >= tabCount) {
          tabFocus = 0;
        }

        setTabFocus(tabFocus);
        break;
      case 'ArrowLeft':
        event.preventDefault();
        tabFocus = tabFocusIndex - 1;

        if (tabFocus < 0) {
          tabFocus = tabCount - 1;
        }

        setTabFocus(tabFocus);
        break;
      case 'Home':
        event.preventDefault();
        tabFocus = 0;
        setTabFocus(tabFocus);
        break;
      case 'End':
        event.preventDefault();
        tabFocus = tabCount - 1;
        setTabFocus(tabFocus);
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.nutritionTableContainer}>
      {tabData.tabTwo && (
        <div
          role="tablist"
          aria-labelledby="nutrition-product-heading"
          className={`${styles.nutritionTabs} ${tabActiveIndex === 0 ? styles.nutritionTabLeft : ''}`}
        >
          {Object.values(tabData).map((_tableData: unknown, i: number) => {
            return (
              <button
                role="tab"
                key={`key-${i}`}
                id={`nutrition-tab-${i}`}
                onClick={() => tabOnClicked(i)}
                onFocus={() => setTabFocusIndex(i)}
                onKeyDown={onKeyDown}
                aria-controls={`nutrition-panel-${i}`}
                aria-selected={tabActiveIndex === i}
                tabIndex={tabActiveIndex === i ? 0 : -1}
                className={`${styles.nutritionTab} ${tabActiveIndex === i ? styles.nutritionActiveTab : ''}`}
                ref={(el: HTMLButtonElement | null) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  el && (tabRefs.current[i] = el);
                }}
              >
                {tabBtnText[i] && (
                  <span className={`${styles.nutritionTabText}`}>{tabBtnText[i]}</span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {tabData.tabOne.servingsPer && !isGMFSProduct && (
        <div className={styles.nutritionServingPc}>
          {servingsPerText}:{' '}
          <EnhancedRichText value={tabData.tabOne.servingsPer} parentElementType="span" />
        </div>
      )}

      {tabData.tabOne?.servingSize && (
        <div className={styles.nutritionServingSize}>
          {servingSizeText}:{' '}
          <EnhancedRichText value={tabData.tabOne.servingSize} parentElementType="span" />
        </div>
      )}

      {Object.values(tabData)
        .filter((tableData) => tableData !== null)
        .map((tableData, i: number) => {
          // skip map if tableData is null

          return (
            <div
              id={`nutrition-panel-${i}`}
              key={`panel-${i}`}
              {...(tabData.tabTwo && {
                role: 'tabpanel',
                tabIndex: 0,
                'aria-labelledby': `nutrition-tab-${i}`,
                hidden: tabActiveIndex !== i,
              })}
              className={`${styles.nutritionTabContent} ${tabActiveIndex === i ? styles.nutritionActiveTabContent : ''}`}
            >
              <div className={`${styles.nutritionCaloriesAndAmount}`} aria-hidden="true">
                <div className={`${styles.nutritionCaloriesColumn}`}>
                  <span className={`${styles.nutritionCaloriesEyebrowText}`}>
                    {amountPerServingText}
                  </span>
                  <span className={`${styles.nutritionCalories}`}>{caloriesText}</span>
                </div>
                <div className={`${styles.nutritionCaloriesAmountColumn}`}>
                  <span className={`${styles.nutritionCaloriesAmount}`}>
                    {tableData?.calories != null && tableData?.calories !== '' ? (
                      tableData.calories
                    ) : (
                      <span className={`${styles.nutritionCaloriesAmountEmpty}`}>
                        <span aria-hidden="true">&mdash;</span>
                        <span className="screenReaderText">{valueNotAvailableText}</span>
                      </span>
                    )}
                  </span>
                  {!isGMFSProduct && tabSubText[i] && (
                    <span className={`${styles.nutritionCaloriesAmountSubText}`}>
                      {tabSubText[i]}
                    </span>
                  )}
                </div>
              </div>

              <div className="screenReaderText">
                {amountPerServingText} {caloriesText} {tabSubText[i]}:{' '}
                {tableData?.calories != null && tableData?.calories !== ''
                  ? tableData?.calories
                  : valueNotAvailableText}
              </div>

              <table className={`${styles.nutritionTable}`}>
                <caption className="screenReaderText">{nutritionHeadline}</caption>
                <thead>
                  <tr className={`${styles.nutritionDailyValue} ${styles.borderGapBoth}`}>
                    <th className={`${styles.nutritionTableHeading}`} scope="col">
                      <span className={styles.nutritionTableHeadingLabel}>
                        {foodNutrientHeading}
                      </span>
                    </th>
                    <th className={`${styles.nutritionTableHeading}`} scope="col">
                      <span className={styles.nutritionTableHeadingLabel}>{amountHeading}</span>
                      {tabSubText[i] && (
                        <>
                          <span className="screenReaderText">,&nbsp;</span>
                          <span className={`${styles.nutritionCaloriesAmountSubText}`}>
                            {tabSubText[i]}
                          </span>
                        </>
                      )}
                    </th>
                    <th className={`${styles.nutritionTableHeading}`} scope="col">
                      <span className={styles.nutritionTableHeadingLabel}>
                        &#37; <span aria-hidden="true">{dailyValueHeading}</span>
                        <span className="screenReaderText">{`, ${dailyValueText}`}</span>
                        {disclaimerIndicators}
                      </span>
                      {tabSubText[i] && (
                        <>
                          <span className="screenReaderText">,&nbsp;</span>
                          <span className={`${styles.nutritionCaloriesAmountSubText}`}>
                            {tabSubText[i]}
                          </span>
                        </>
                      )}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {tableData?.nutritionItems &&
                    tableData?.nutritionItems.map((item: INutritionItem, index: number) => {
                      const rowClass =
                        item.rowType === 'subitem' || item.rowType === 'addedsugars'
                          ? `${styles.nutritionSubitem}`
                          : '';
                      const rowBorderClass =
                        index === 0
                          ? `${styles.borderGapTop}`
                          : index === (tabData.tabOne.nutritionItems?.length ?? 0) - 1
                            ? `${styles.borderGapBottom}`
                            : '';
                      return (
                        <tr key={index} className={`${rowClass} ${rowBorderClass}`}>
                          {item.rowType === 'addedsugars' ? (
                            <th scope="row">{item.displayName}</th>
                          ) : (
                            <th className={`${styles.nutritionItemBold}`} scope="row">
                              {item.displayName}
                            </th>
                          )}

                          <td>
                            {item.quantity ? (
                              <EnhancedRichText value={item.quantity} parentElementType="span" />
                            ) : (
                              <span>
                                <span aria-hidden="true">&mdash;</span>
                                <span className="screenReaderText">{valueNotAvailableText}</span>
                              </span>
                            )}
                          </td>
                          <td>
                            {item.percent ? (
                              <EnhancedRichText value={item.percent} parentElementType="span" />
                            ) : (
                              <span>
                                <span aria-hidden="true">&mdash;</span>
                                <span className="screenReaderText">
                                  &#37; {valueNotAvailableText}
                                </span>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}

                  {tableData?.vitaminItems &&
                    tableData?.vitaminItems.map((item: IVitaminItem, index: number) => {
                      const rowBorderClass =
                        index === 0
                          ? `${styles.borderGapTop} ${styles.nutritionVitamins}`
                          : index === (tabData.tabOne.vitaminItems?.length ?? 0) - 1
                            ? `${styles.borderGapBottom}`
                            : '';
                      return (
                        <tr className={`${rowBorderClass}`} key={index}>
                          <th scope="row">{item.displayName}</th>

                          <td>
                            {item.quantity ? (
                              <EnhancedRichText value={item.quantity} parentElementType="span" />
                            ) : (
                              <span>
                                <span aria-hidden="true">&mdash;</span>
                                <span className="screenReaderText">{valueNotAvailableText}</span>
                              </span>
                            )}
                          </td>
                          <td>
                            {item.percent ? (
                              <EnhancedRichText value={item.percent} parentElementType="span" />
                            ) : (
                              <span>
                                <span aria-hidden="true">&mdash;</span>
                                <span className="screenReaderText">
                                  &#37; {valueNotAvailableText}
                                </span>
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>

                {defaultDisclaimerText && (
                  <tfoot className={styles.nutritionDisclaimer}>
                    {additionalDisclaimers.length > 0 &&
                      additionalDisclaimers.map((item: IDisclaimer, index: number) => {
                        return (
                          <tr key={index}>
                            <td colSpan={3}>
                              <div className={styles.nutritionDisclaimerTextWrap}>
                                {String.fromCharCode(0x002a).repeat(index + 1)}
                                <EnhancedRichText
                                  value={item.additionalNutritionDisclaimerText}
                                  className={styles.nutritionDisclaimerText}
                                  parentElementType="span"
                                />
                              </div>
                            </td>
                          </tr>
                        );
                      })}

                    {defaultDisclaimerText && (
                      <tr>
                        <td colSpan={3}>
                          <div className={styles.nutritionDisclaimerTextWrap}>
                            {disclaimerIndicators}
                            <EnhancedRichText
                              value={defaultDisclaimerText}
                              className={styles.nutritionDisclaimerText}
                              parentElementType="span"
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </tfoot>
                )}
              </table>
            </div>
          );
        })}
    </div>
  );
}
